// src/components/common/ReactSelect.jsx
import React from 'react';
import Select from 'react-select';
import { useTheme } from 'next-themes';

const colourStyles = {
  control: (styles, { isDisabled, isFocused }) => ({
    ...styles,
    backgroundColor: isDisabled ? 'var(--muted)' : 'var(--card)',
    borderColor: isFocused ? 'var(--primary)' : 'var(--border)',
    boxShadow: isFocused ? '0 0 0 1px var(--primary)' : 'none',
    borderRadius: 'var(--radius-md)',
    padding: '2px 4px',
    minHeight: '40px',
    ':hover': {
      borderColor: 'var(--primary)',
    },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    zIndex: 9999,
  }),
  menuList: (styles) => ({
    ...styles,
    padding: 0,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected
      ? 'var(--primary)'
      : isFocused
        ? 'var(--accent)'
        : 'transparent',
    color: isSelected ? 'var(--primary-foreground)' : 'var(--foreground)',
    padding: '10px 12px',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: 'var(--primary-foreground)',
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'var(--foreground)',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--muted-foreground)',
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: 'var(--muted-foreground)',
    ':hover': {
      color: 'var(--foreground)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--foreground)',
  }),
};

const ReactSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option...",
  isDisabled = false,
  isLoading = false,
  isClearable = false,
  label,
  className = "",
  width = "100%",
  minWidth = "",
  maxWidth = "",
  containerClassName = "",
  maxMenuHeight = 200,
  controlHeight,
}) => {
  const { theme } = useTheme();

  const customStyles = {
    ...colourStyles,
    control: (base, state) => ({
      ...colourStyles.control(base, state),
      width: width,
      minWidth: minWidth || undefined,
      maxWidth: maxWidth || undefined,
      ...(controlHeight && { minHeight: controlHeight, padding: 0 }),
    }),
    valueContainer: (styles) => ({
      ...styles,
      ...(controlHeight && { padding: '0 8px' }),
    }),
    clearIndicator: (styles) => ({
      ...styles,
      ...(controlHeight && { padding: '0 4px' }),
    }),
    dropdownIndicator: (styles) => ({
      ...colourStyles.dropdownIndicator(styles),
      ...(controlHeight && { padding: '0 6px' }),
    }),
    menuList: (styles) => ({
      ...colourStyles.menuList(styles),
      maxHeight: maxMenuHeight,
    }),
  };

  return (
    <div className={`w-full ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
        </label>
      )}
      <div className={className}>
        <Select
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          styles={customStyles}
          theme={(defaultTheme) => ({
            ...defaultTheme,
            colors: {
              ...defaultTheme.colors,
              primary: 'var(--primary)',
              primary75: 'var(--primary)',
              primary50: 'var(--accent)',
              primary25: 'var(--muted)',
              neutral0: 'var(--card)',
              neutral5: 'var(--muted)',
              neutral10: 'var(--border)',
              neutral20: 'var(--border)',
              neutral30: 'var(--muted-foreground)',
              neutral40: 'var(--foreground)',
              neutral50: 'var(--foreground)',
              neutral60: 'var(--foreground)',
              neutral70: 'var(--foreground)',
              neutral80: 'var(--foreground)',
              neutral90: 'var(--foreground)',
            },
          })}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
    </div>
  );
};

export default ReactSelect;